import dayjs from 'dayjs';

/**
 * Formats input string to DD/MM/YYYY date mask
 * Allows editing individual date parts independently without auto-jumping
 * @param value - Raw input string
 * @returns Formatted string in DD/MM/YYYY format
 */
export const dateMask = (value: string): string => {
  // Remove all non-numeric characters
  const numbers = value.replace(/[^0-9]/g, '');

  // If empty, return empty string
  if (!numbers) return '';

  // Apply formatting based on length
  if (numbers.length <= 2) {
    return numbers;
  }
  if (numbers.length <= 4) {
    return `${numbers.slice(0, 2)}/${numbers.slice(2)}`;
  }
  return `${numbers.slice(0, 2)}/${numbers.slice(2, 4)}/${numbers.slice(4, 8)}`;
};

/**
 * Parses a masked date string (DD/MM/YYYY) back to a Date object
 * @param maskedValue - Date string in DD/MM/YYYY format
 * @param fallbackDate - Date to return if parsing fails
 * @returns Valid Date object or fallbackDate
 */
export const parseDateFromMask = (maskedValue: string, fallbackDate: Date): Date => {
  // Remove any non-numeric characters except slashes
  const cleanValue = maskedValue.replace(/[^0-9/]/g, '');

  // Split by slash
  const parts = cleanValue.split('/');

  // Need exactly 3 parts for a complete date
  if (parts.length !== 3) {
    return fallbackDate;
  }

  const [day, month, year] = parts;

  // Validate parts exist and have reasonable lengths
  if (!day || !month || !year || year.length !== 4) {
    return fallbackDate;
  }

  // Parse to numbers
  const dayNum = parseInt(day, 10);
  const monthNum = parseInt(month, 10);
  const yearNum = parseInt(year, 10);

  // Basic validation
  if (
    dayNum < 1 ||
    dayNum > 31 ||
    monthNum < 1 ||
    monthNum > 12 ||
    yearNum < 1900 ||
    yearNum > 2100
  ) {
    return fallbackDate;
  }

  // Create dayjs object and validate it's a real date
  const parsedDate = dayjs(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);

  if (!parsedDate.isValid()) {
    return fallbackDate;
  }

  return parsedDate.toDate();
};

/**
 * Validates if a masked date string represents a valid date
 * @param maskedValue - Date string in DD/MM/YYYY format
 * @returns true if valid, false otherwise
 */
export const isValidDateMask = (maskedValue: string): boolean => {
  const cleanValue = maskedValue.replace(/[^0-9/]/g, '');
  const parts = cleanValue.split('/');

  if (parts.length !== 3) return false;

  const [day, month, year] = parts;

  if (!day || !month || !year || year.length !== 4) return false;

  const dayNum = parseInt(day, 10);
  const monthNum = parseInt(month, 10);
  const yearNum = parseInt(year, 10);

  if (
    dayNum < 1 ||
    dayNum > 31 ||
    monthNum < 1 ||
    monthNum > 12 ||
    yearNum < 1900 ||
    yearNum > 2100
  ) {
    return false;
  }

  const parsedDate = dayjs(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
  return parsedDate.isValid();
};
